import { useState, useEffect, useRef, useLayoutEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import { Layout, Space, Button, Table, Tooltip, Drawer, Alert, Divider, Dropdown, Modal, Form, Input, message, Switch, Typography } from 'antd';
import { useHorizontalScrollGuard } from '@/hooks/useHorizontalScrollGuard';
import { useTableScrollY } from '@/hooks/useTableScrollY';
import { useVerticalScrollShadow } from '@/hooks/useVerticalScrollShadow';
import { useEnableTableScrollYLayout } from '@/layouts/tableScrollLayout';
import { getProjestsApi, getProjestsTopicsListApi, delTopicsApi, saveFieldApi, saveTopicsApi, saveDirectionsApi, getDirectionsInfoApi, updateDirectionsInfoApi, getTopicsInfoApi, updateTopicsApi, getProjectPosterModulesListAllApi } from '@services/Selection';
import PageHeader from '@components/PageHeader/PageHeader';
import './index.less';
import SortTopic from './components/SortTopic';
import FieldsList from './components/FieldsList';
import Declare from './components/Declare';
import PosterSettingDrawer from './components/PosterSettingDrawer';
import { workspaceSelectionDeclarationAddPath, workspaceSelectionDeclarationEditPath } from '@/layouts/selectionWorkspacePaths';

const { Text } = Typography;

const TopicsList = () => {
    const location = useLocation();
    const params = useParams();
    const navigate = useNavigate();
    const [firstForm] = Form.useForm();
    const [directionForm] = Form.useForm();

    const [data, setData] = useState([]);
    const [allFields, setAllFields] = useState([]);
    const [fieldsSelect, setFieldsSelect] = useState([]);

    const [firstShow, setFirstShow] = useState(false);
    const [fieldsShow, setFieldsShow] = useState(false);
    const [declareShow, setDeclareShow] = useState(false);
    const [sortShow, setSortShow] = useState(false);
    const [declareModal, setDeclareModal] = useState(false);

    const [topicsName, setTopicsName] = useState(undefined);
    const [topicId, setTopicId] = useState(undefined);
    const [topicIds, setTopicIds] = useState(undefined);
    const [declareId, setDeclareId] = useState(undefined);
    const [directions, setDirections] = useState(undefined);
    const [posterSettingShow, setPosterSettingShow] = useState(false);
    const [posterEnabled, setPosterEnabled] = useState(false);
    const [projectInfo, setProjectInfo] = useState(null);

    const tableRegionRef = useRef(null);
    const tableScrollContainerRef = useRef(null);
    const [scrollContainerVersion, setScrollContainerVersion] = useState(0);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const enableTableScrollYLayout = useEnableTableScrollYLayout();
    const tableDeductSelectors = useMemo(() => ['.main-toolbar'], []);
    const tableScrollY = useTableScrollY(tableRegionRef, {
        enabled: enableTableScrollYLayout || isFullscreen,
        heightRootClassName: 'main-container-card',
        scrollYOffset: 0,
        deductSiblingSelectors: tableDeductSelectors,
    });

    useHorizontalScrollGuard(tableScrollContainerRef, { threshold: 6, edgeHoldMs: 220, watchValue: scrollContainerVersion });
    useVerticalScrollShadow(tableScrollContainerRef, { threshold: 2, watchValue: scrollContainerVersion });

    useLayoutEffect(() => {
        const region = tableRegionRef.current;
        if (!region) return;
        const resolveScrollContainer = () => {
            const next = region.querySelector('.ant-table-body') ?? region.querySelector('.ant-table-content');
            if (tableScrollContainerRef.current !== next) {
                tableScrollContainerRef.current = next;
                setScrollContainerVersion(v => v + 1);
            }
        };
        resolveScrollContainer();
        let rafId = 0;
        const mutationObserver = new MutationObserver(() => {
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => {
                rafId = 0;
                resolveScrollContainer();
            });
        });
        mutationObserver.observe(region, { childList: true, subtree: true });
        return () => {
            if (rafId) cancelAnimationFrame(rafId);
            mutationObserver.disconnect();
        };
    }, []);

    const handleToggleFullscreen = useCallback(() => {
        const target = document.querySelector('main.main-container');
        if (!target) return;
        const next = !target.classList.contains('is-page-fullscreen');
        target.classList.toggle('is-page-fullscreen', next);
        setIsFullscreen(next);
    }, []);

    useEffect(() => {
        const onKeyDown = e => {
            if (e.key !== 'Escape') return;
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (!target) return;
            target.classList.remove('is-page-fullscreen');
            setIsFullscreen(false);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    useEffect(() => {
        return () => {
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (target) target.classList.remove('is-page-fullscreen');
        };
    }, []);

    const columns = [
        { title: '主题', dataIndex: 'name', width: 200 },
        {
            title: '主题说明',
            dataIndex: 'description',
            width: 200,
            render: (_, row) => {
                if (row?.pid === null) return null;
                if (!row?.description) return <Text type="secondary">&ndash;</Text>;
                return row.description;
            },
        },
        {
            title: '主题方向',
            dataIndex: 'topicDirectionList',
            width: 80,
            render: (_, row) => {
                return <>{row?.pid === null ? null : <>{row?.topicDirectionList && row?.topicDirectionList.length > 0 ? <span>{row?.topicDirectionList.length}个</span> : '-'}</>}</>;
            },
        },
        {
            title: (
                <div>
                    <span>申报实践数</span>
                    <Tooltip className="case-num-box" title="审核通过数 / 总数(待审核+已通过+已驳回)">
                        <i className="bi bi-question-circle" />
                    </Tooltip>
                </div>
            ),
            dataIndex: 'countInfo',
            width: 120,
            render: (_, row) => {
                return (
                    <>
                        {row?.pid === null ? null : (
                            <>
                                <span style={{ color: '#76c372' }}>{row.passTotal || 0}</span>/<span>{row.caseTotal || 0}</span>
                            </>
                        )}
                    </>
                );
            },
        },
        {
            title: '显示状态',
            dataIndex: 'hide',
            width: 80,
            render: (_, row) => {
                return <>{row?.hide !== null && row?.hide === true ? <span style={{ color: '#febe88' }}>隐藏</span> : <span style={{ color: '#76c372' }}>展示</span>}</>;
            },
        },
        {
            title: '操作',
            dataIndex: 'operations',
            width: 240,
            render: (_, row) => {
                const items = [
                    {
                        label: '删除主题',
                        key: 'delete',
                        onClick: () => {
                            delTopic(row);
                        },
                        danger: true,
                    },
                ];
                return (
                    <Space className="operation-btns" separator={<Divider orientation="vertical" />}>
                        {row?.pid === null ? (
                            <Button size="small" type="link" onClick={() => (setFirstShow(true), setTopicIds(row.id))}>
                                编辑主题分类
                            </Button>
                        ) : (
                            <Button size="small" type="link" onClick={() => navigate(workspaceSelectionDeclarationEditPath(params.pid, row.id), { state: { ...location.state } })}>
                                编辑主题
                            </Button>
                        )}

                        {row?.pid === null ? (
                            ''
                        ) : (
                            <>
                                <Button
                                    size="small"
                                    type="link"
                                    onClick={() => {
                                        setFieldsShow(true);
                                        setTopicId(row.id);
                                    }}
                                >
                                    申报字段
                                </Button>
                                <Button
                                    size="small"
                                    type="link"
                                    onClick={() => {
                                        setDeclareShow(true);
                                        setTopicId(row.id);
                                        setTopicsName(row.name);
                                    }}
                                >
                                    主题方向
                                </Button>
                            </>
                        )}
                        {row?.children && row?.children.length > 0 ? (
                            ''
                        ) : (
                            <>
                                <Dropdown menu={{ items }} placement="bottomRight">
                                    <Button type="link" size="small" className="more-operation">
                                        <i className="bi bi-three-dots" />
                                    </Button>
                                </Dropdown>
                            </>
                        )}
                    </Space>
                );
            },
        },
    ];

    const onClose = type => {
        console.log(1);
        switch (type) {
            case 'sort':
                setSortShow(false);
                break;
            case 'fields':
                setAllFields([]);
                setFieldsSelect([]);
                setFieldsShow(false);
                break;
            case 'declare':
                setDeclareShow(false);
                onReset();
                break;
            default:
                break;
        }
    };

    const onSaveField = () => {
        const CONTACT_INFO_CATE_ID = 3; // 联系人信息分类ID：3 联系人信息
        const contactFieldsMissingChildren = allFields.some(field => {
            if (field.pid !== CONTACT_INFO_CATE_ID) return false;
            const ch = field.children;
            return !Array.isArray(ch) || ch.length === 0;
        });
        if (contactFieldsMissingChildren) {
            message.error('已选联系人信息字段的二级字段未配置，保存失败！');
            return;
        }
        saveFieldApi({ caseFields: JSON.stringify(allFields), topicId }).then(res => {
            if (res.status_code === 200) {
                setFieldsShow(false);
                onReset();
                message.success('保存成功');
            }
        });
    };

    //保存主题分类
    const onSaveFirst = () => {
        firstForm.validateFields().then(() => {
            if (topicIds) {
                updateTopicsApi({ ...firstForm.getFieldsValue(), projectId: params.pid, id: topicIds }).then(res => {
                    if (res.status_code === 200) {
                        onReset();
                        setFirstShow(false);
                    }
                });
            } else {
                saveTopicsApi({ ...firstForm.getFieldsValue(), projectId: params.pid, hide: firstForm.getFieldValue('hide') || false }).then(res => {
                    if (res.status_code === 200) {
                        onReset();
                        setFirstShow(false);
                    }
                });
            }
        });
    };

    //保存主题方向
    const onSaveDirections = () => {
        if (declareId) {
            updateDirectionsInfoApi({ ...directions, ...directionForm.getFieldsValue() }).then(res => {
                if (res.status_code === 200) {
                    setDeclareModal(false);
                }
            });
        } else {
            directionForm.validateFields().then(() => {
                saveDirectionsApi({ ...directionForm.getFieldsValue(), projectId: params?.pid, topicId }).then(res => {
                    if (res.status_code === 200) {
                        setDeclareModal(false);
                    }
                });
            });
        }
    };

    //删除主题
    const delTopic = delid => {
        if (delid.caseTotal > 0) {
            message.error('该主题下存在实践数据，无法删除！');
        } else {
            Modal.confirm({
                title: '提示',
                content: '确认删除该主题',
                cancelText: '取消',
                okText: '确认',
                closable: true,
                onOk: () => {
                    delTopicsApi({ topicId: delid.id }).then(res => {
                        if (res.status_code === 200) {
                            onReset();
                        }
                    });
                },
            });
        }
    };

    const onReset = () => {
        getProjestsTopicsListApi({ projectId: params.pid }).then(topics => {
            if (topics.status_code === 200) {
                setData(topics.data);
            }
        });
    };

    useEffect(() => {
        onReset();
    }, []);

    // 获取项目基本信息用于 breadcrumb 展示
    useEffect(() => {
        if (params.pid && !projectInfo) {
            getProjestsApi({ id: params.pid }).then(res => {
                if (res.status_code === 200) {
                    setProjectInfo(res.data);
                }
            });
        }
    }, [params.pid]);

    // 获取壁报征集状态
    useEffect(() => {
        if (params.pid) {
            getProjectPosterModulesListAllApi({ projectId: params.pid }).then(res => {
                if (res.status_code === 200 && res.data?.project) {
                    setPosterEnabled(res.data.project.posterEnabled || false);
                }
            });
        }
    }, [params.pid]);

    useEffect(() => {
        document.title = '主题管理-医评议';
    }, []);

    // 主题方向
    useEffect(() => {
        if (declareModal) {
            if (declareId !== undefined) {
                getDirectionsInfoApi({ id: declareId }).then(res => {
                    if (res.status_code === 200) {
                        directionForm.setFieldsValue(res.data);
                        setDirections(res?.data);
                    }
                });
            } else {
                directionForm.setFieldValue('description', '');
            }
        }
    }, [declareModal]);

    // 一级主题分类
    useEffect(() => {
        if (firstShow) {
            if (topicIds !== undefined) {
                getTopicsInfoApi({ id: topicIds }).then(res => {
                    if (res.status_code === 200) {
                        firstForm.setFieldsValue(res.data);
                        // setDirections(res?.data);
                    }
                });
            } else {
                firstForm.setFieldValue('name', '');
                firstForm.setFieldValue('hide', false);
            }
        }
    }, [firstShow]);

    return (
        <div className="page-container list-page-container selection-theme-page">
            <PageHeader title="申报管理" breadcrumb={[{ label: '医评议', path: '/selection/projects' }, { label: projectInfo?.name }, { label: '申报管理' }]} />

            <div ref={tableRegionRef} className="table-region-container selection-theme-table-region">
                <div className="main-toolbar">
                    <div className="main-tools">
                        <Space>
                            <Button type="primary" onClick={() => navigate(workspaceSelectionDeclarationAddPath(params.pid))}>
                                新增主题
                            </Button>
                            <Button
                                onClick={() => {
                                    setFirstShow(true);
                                    setTopicIds(undefined);
                                }}
                            >
                                新增主题分类
                            </Button>
                            <Button
                                onClick={() => {
                                    setSortShow(true);
                                }}
                            >
                                主题排序
                            </Button>
                        </Space>
                    </div>
                    <div className="sub-tools">
                        <Space>
                            <span>
                                壁报征集状态：<span style={{ color: posterEnabled ? '#76c372' : '#faad14' }}>{posterEnabled ? '开启' : '关闭'}</span>
                            </span>
                            <Button type="link" onClick={() => setPosterSettingShow(true)}>
                                壁报征集设置
                            </Button>
                            <Tooltip title="刷新列表">
                                <Button type="default" onClick={onReset} icon={<span className="bi bi-arrow-clockwise" aria-hidden="true" />} aria-label="刷新列表" />
                            </Tooltip>
                            <Tooltip title={isFullscreen ? '退出全屏' : '全屏'}>
                                <Button type="default" onClick={handleToggleFullscreen} icon={<span className={isFullscreen ? 'bi bi-fullscreen-exit' : 'bi bi-fullscreen'} aria-hidden="true" />} aria-label={isFullscreen ? '退出全屏' : '全屏'} />
                            </Tooltip>
                        </Space>
                    </div>
                </div>

                <div className="list-table-wrap selection-theme-table-wrap">
                    <Table
                        className={tableScrollY != null ? 'custom-table-scrollbar-always topics-table' : 'topics-table'}
                        rowKey={item => item.id}
                        columns={columns}
                        dataSource={data}
                        expandable={{ defaultExpandAllRows: true }}
                        pagination={false}
                        scroll={{ x: 'max-content', ...(tableScrollY != null ? { y: tableScrollY } : {}) }}
                    />
                </div>
            </div>

            <Drawer title="主题排序" placement="right" open={sortShow} destroyOnHidden closeIcon={null} size={500} extra={<i className="bi bi-x-lg drawer-close-icon" onClick={() => onClose('sort')} />}>
                <Alert
                    title={
                        <>
                            <div>把主题拖动到合适顺序的位置，完成排序。</div>
                            <div>注意：主题排序限同级，不能改变主题从属关系。</div>
                        </>
                    }
                    type="warning"
                ></Alert>
                <SortTopic tableData={data} onReset={onReset} />
            </Drawer>

            <Drawer
                title="申报字段"
                placement="bottom"
                open={fieldsShow}
                destroyOnHidden
                closeIcon={null}
                size="100%"
                extra={<i className="bi bi-x-lg drawer-close-icon" onClick={() => onClose('fields')} />}
                footer={
                    <div className="topics-drawer-footer">
                        <Space>
                            <Button onClick={() => onClose('fields')}>取消</Button>
                            <Button type="primary" onClick={onSaveField}>
                                确认
                            </Button>
                        </Space>
                    </div>
                }
            >
                <FieldsList topicId={topicId} allFields={allFields} setAllFields={setAllFields} fieldsSelect={fieldsSelect} setFieldsSelect={setFieldsSelect} />
            </Drawer>

            <Drawer title="主题方向" placement="right" open={declareShow} destroyOnClose closeIcon={null} size={500} extra={<i className="bi bi-x-lg drawer-close-icon" onClick={() => onClose('declare')} />}>
                <div>主题：{topicsName}</div>
                <div>主题方向：</div>
                <div>
                    <Button type="link" onClick={() => (setDeclareModal(true), setDeclareId(undefined))}>
                        <i className="bi bi-plus-circle" />
                        新增主题方向
                    </Button>
                </div>
                <Declare topicId={topicId} setDeclareModal={setDeclareModal} setDeclareId={setDeclareId} declareModal={declareModal} />
            </Drawer>

            <Modal className="topicModalClassName" title={declareId === undefined ? '新增主题方向' : '编辑主题方向'} open={declareModal} onCancel={() => setDeclareModal(false)} destroyOnHidden zIndex={9000} mask={{ closable: false }} onOk={onSaveDirections}>
                <Form layout="vertical" form={directionForm}>
                    <Form.Item
                        label="主题方向"
                        name={'description'}
                        rules={[
                            {
                                required: true,
                                message: '请填写主题方向',
                            },
                        ]}
                    >
                        <Input showCount maxLength={100} placeholder="请添加主题方向" />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal title={topicIds === undefined ? '新增主题分类' : '编辑主题分类'} open={firstShow} onCancel={() => setFirstShow(false)} destroyOnHidden zIndex={9000} mask={{ closable: false }} onOk={onSaveFirst}>
                <Form layout="vertical" form={firstForm}>
                    <Form.Item
                        label="主题分类名称"
                        name={'name'}
                        rules={[
                            {
                                required: true,
                                message: '请输入主题分类名称',
                            },
                            {
                                pattern: /^.{2,30}$/,
                                message: '主题分类名称 2-30 个字符',
                            },
                        ]}
                    >
                        <Input showCount minLength={2} maxLength={30} placeholder="请输入主题分类名称" />
                    </Form.Item>
                    <Form.Item label="是否隐藏" name={'hide'} valuePropName={'checked'}>
                        <Switch checkedChildren="是" unCheckedChildren="否" />
                    </Form.Item>
                </Form>
            </Modal>

            {/* 壁报征集设置 Drawer */}
            <PosterSettingDrawer
                open={posterSettingShow}
                onClose={() => setPosterSettingShow(false)}
                onSaveSuccess={() => {
                    // 刷新壁报征集状态
                    if (params.pid) {
                        getProjectPosterModulesListAllApi({ projectId: params.pid }).then(res => {
                            if (res.status_code === 200 && res.data?.project) {
                                setPosterEnabled(res.data.project.posterEnabled || false);
                            }
                        });
                    }
                }}
            />
        </div>
    );
};

export default TopicsList;
